/**
 * 合同管理
 */
import React, { useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { EnumUtil } from '@tencent/adp-fe-common';
import BasePage from '@/business-components/base-page';
import Toolbar from '@/common-components/toolbar';
import ListView from '@/business-components/list-view';
import useListView from '@/common/hooks/use-list-view';
import { downloadPdfFromBase64 } from '@/api/operator/fetch';
import dialog from '@/utils/dialog';
import { IS_PRODUCTION } from '@/constants/config';
import Qrcode from './qrcode';
import { getContractList, downloadContract, getContractQRcode } from './api';
import store from './store';
import { initContractSummary } from './services';

import './index.scoped.less';

export const CONTRACT_STATUS = EnumUtil.createEnumWithAlias({
  unsigned: ['to_be_signed', '待签署'],
  archived: ['archived', '已归档'],
});

const options = ({ payloads, customFunctions }: any) => {
  const { tableColumns } = payloads;
  const { handleSignContract, handleDownloadContract } = customFunctions;

  return {
    fetchConfig: {
      action: getContractList,
      $onRequest: (params: any) => {
        return params;
      },
    },
    tableConfig: {
      columns: tableColumns,
      operations: {
        minWidth: 90,
        maxWidth: 90,
        columnAction: (data: any) => [
          data.actions?.includes('Sign') && {
            label: '去签署',
            action: {
              type: 'custom',
              onClick: () => {
                handleSignContract(data);
              },
            },
            props: {
              ['data-report']: 'sign-contract',
              ['data-report-extra']: `relation_key:contract_no;relation_value:${data.contract_no}`,
            },
          },
          data.actions?.includes('Download') && {
            label: '下载合同',
            action: {
              type: 'custom',
              onClick: () => {
                handleDownloadContract(data);
              },
            },
            props: {
              ['data-report']: 'download-contract',
              ['data-report-extra']: `relation_key:contract_no;relation_value:${data.contract_no}`,
            },
          },
        ],
      },
    },
  };
};

const ContractManage = () => {
  const [contractQrUrl, setContractQrUrl] = useState('');

  const {
    listRef,
    tableColumns,
    filterConf,
    handleColumnsChange,
    initFilterConf,
  } = useListView();

  const handleSignContract = useCallback(async (data: any) => {
    const { contract_no } = data;
    const res: any = await getContractQRcode({ contract_no });
    const { code, trace_id } = res || {};
    let url = res?.data?.tcloud_user_stamp_urls?.[0]?.url || '';
    if (code === 0) {
      if (url.length) {
        if (!IS_PRODUCTION && url.includes('h5-activity-dev')) {
          url = url.replace('h5-activity-dev', 'h5-activity-beta');
        }
        setContractQrUrl(url);
      } else {
        dialog.alert(<div className="dialog-err">
          <span>{res?.data?.message || '您好，目前该合同电子签签署待办生成中，请耐心等待，如30分钟后仍无法签署请联系您的渠道销售经理进行处理。'}</span>
          {trace_id ? <span>{`错误提示码：${trace_id}`}</span> : null}
        </div>);
      }
    }
  }, []);

  const handleDownloadContract = useCallback(async (data: any) => {
    const { contract_id } = data;
    const res: any = await downloadContract({ contract_id });
    const { code, data: resData, trace_id } = res || {};
    if (code === 0) {
      if (resData?.result?.length) {
        downloadPdfFromBase64(res?.data?.result, `${data.agency_sign_subject || ''}${data.contract_type || '合同'}${data.contract_no}.pdf`);
      } else {
        dialog.alert(<div className="dialog-err">
          <span>{res?.data?.message || '获取合同失败'}</span>
          {trace_id ? <span>{`错误提示码：${trace_id}`}</span> : null}
        </div>);
      }
    }
  }, []);

  useEffect(() => {
    initContractSummary();
  }, []);

  return <BasePage
    bodyComponent={<div className="contract-manage">
      <div className="contract-summary">
        {_.map(CONTRACT_STATUS as any, (status: string) => {
          return <div key={status} className="summary-item">
            <span>{CONTRACT_STATUS.getDescByValue(status)}合同数：</span>
            <span className={status === CONTRACT_STATUS.unsigned ? 'red' : ''}>{!_.isNil(store.summaryInfoMap[status]) ? store.summaryInfoMap[status] : '-'}</span>
          </div>;
        })}
      </div>
      <Toolbar
        right={[
          Toolbar.TOOL_TYPE.columns,
        ]}
        columnsOptions={{
          useWuji: true,
          editable: false,
          module: 'contract_manage',
          updateSelectedColumns: handleColumnsChange,
          onColumnsLoaded: initFilterConf,
          haveLabel: false,
        }}
      />
      <ListView
        listRef={listRef}
        options={options}
        columns={tableColumns}
        additionalFilterConfig={filterConf}
        defaultFilterKeys={[
          'contract_no',
          'contract_status',
          'contract_type',
          {
            key: 'date',
            title: '签署时间',
            nameMap: {
              start_time: 'sign_begin_date',
              end_time: 'sign_end_date',
            },
          },
        ]}
        payloads={{
          listRef,
          tableColumns,
        }}
        customFunctions={{
          handleSignContract,
          handleDownloadContract,
        }}
      />
      <Qrcode url={contractQrUrl} onClose={() => setContractQrUrl('')} />
    </div>}
  />;
};

export default ContractManage;
